import { usePersistedState } from '@cbgms/base/hooks/persisted-state/use-persisted-state'
import { IProductWithState } from '@cbgms/api'
import React, { memo, useCallback, useMemo } from 'react'
import { areEqualProducts } from 'app/products/components/ProductListV2/util/product-equal'

interface IStoredProducts {
  [storageKey: string]: { products: IProductWithState[]; dateUpdated: string }
}

export const buildStorageKey = (workorderUUID: string, orderAccountUUID?: string): string => {
  const storageKey = `${workorderUUID}__${orderAccountUUID || 'ALL'}`
  return storageKey
}

const minimumDate = new Date(-8640000000000000)
const clearKeyAndCleanUpOldEntries = (allStoredProducts: IStoredProducts, keyToClear: string): IStoredProducts => {
  const currentDate = new Date()
  const msInDay = 1000 * 60 * 60 * 24
  return Object.entries(allStoredProducts).reduce((out, [key, val]) => {
    const storedDate = val.dateUpdated ? new Date(val.dateUpdated) : minimumDate
    const daysSinceUpdate = Math.abs(currentDate.getTime() - storedDate.getTime()) / msInDay
    // remove unwanted records
    if (keyToClear === key || daysSinceUpdate > 30) {
      return { ...out }
    }
    // keep records that are less then 10 days old
    return { ...out, [key]: val }
  }, {})
}

/**
 * useStoredProductsPersister is an internal function that writes to local storage
 * @returns a set of methods to modify the local storage
 */
const useStoredProductsPersister = () => {
  // store state for each order account
  const [allStoredProducts, setAllStoredProducts] = usePersistedState<IStoredProducts>('stored-products', {})
  const togglePart = useCallback(
    (workorderUUID: string, product: IProductWithState) => {
      const storageKey = buildStorageKey(workorderUUID, product?.OrderAccountUUID)
      const existingValue = allStoredProducts[storageKey]
      // figure out if this was and add or remove
      const items = existingValue?.products || []
      const itemsWhenRemoving = [...items.filter(prod => !areEqualProducts(prod, product))]
      const isAdding = itemsWhenRemoving.length === items.length // when lengths are equal, item  not yet in the list and it should be added
      const newProductList = isAdding ? [...items, product] : itemsWhenRemoving
      setAllStoredProducts({
        ...allStoredProducts,
        [storageKey]: { ...existingValue, products: newProductList, dateUpdated: new Date().toISOString() }
      })
    },
    [allStoredProducts, setAllStoredProducts]
  )

  const clearForWorkorderAndOrderAccount = useCallback(
    (workorderUUID: string, orderAccountUUID: string) => {
      const storageKey = buildStorageKey(workorderUUID, orderAccountUUID)
      const newValue = clearKeyAndCleanUpOldEntries(allStoredProducts, storageKey)
      setAllStoredProducts(newValue)
    },
    [setAllStoredProducts, allStoredProducts]
  )

  return useMemo(
    () => ({ togglePart, allStoredProducts, clearForWorkorderAndOrderAccount }),
    [togglePart, allStoredProducts, clearForWorkorderAndOrderAccount]
  )
}

interface IStoredProductsContextValue {
  togglePart: (workorderUUID: string, input: IProductWithState) => void
  clearForWorkorderAndOrderAccount: (workorderUUID: string, orderAccountUUID: string) => void
  allStoredProducts: IStoredProducts
  hasBeenProvided: boolean
}

export const StoredProductsContext = React.createContext<IStoredProductsContextValue | null>(null)
const MemoStoredProductsContextProvider: React.FC<{ value: IStoredProductsContextValue }> = memo(({ value, children }) => {
  return <StoredProductsContext.Provider value={value}>{children}</StoredProductsContext.Provider>
})

const _StoredProductsContextProvider: React.FC = ({ children }) => {
  const storedState = useStoredProductsPersister()
  const value = useMemo(() => ({ ...storedState, hasBeenProvided: true }), [storedState])
  return <MemoStoredProductsContextProvider value={value}>{children}</MemoStoredProductsContextProvider>
}
const StoredProductsContextProvider: React.FC = memo(_StoredProductsContextProvider)

/**
 * useStoredProductsContext contains all the stored information, you probably want to use useStoredProducts hook instead
 * @returns
 */
const useStoredProductsContext = () => {
  const context = React.useContext(StoredProductsContext)
  if (context === undefined || !context?.hasBeenProvided) {
    throw new Error('useStoredProductsContext must be used within a StoredProductsContext and hasBeenProvided must be set')
  }
  return context
}

export { StoredProductsContextProvider, useStoredProductsContext }
